// Staff-side case management forms — extracted from "Full Circle Immigration Case Management System" (forms 01-09).
// Forms 01-07 are fillable per client case. 08 is a reference SOP. 09 is a per-staff log.

const header = (extra = []) => ({
  title: "Matter Reference",
  fields: [
    { key: "a_number", label: "A-Number", type: "text" },
    { key: "prepared_by", label: "Prepared by", type: "text" },
    { key: "date", label: "Date", type: "date" },
    { key: "attorney_reviewer", label: "Attorney reviewer", type: "text" },
    ...extra,
  ],
});

const reviewBlock = {
  title: "Attorney Review",
  fields: [
    { key: "assessment", label: "Assessment", type: "textarea" },
    { key: "additional_evidence", label: "Additional evidence needed", type: "textarea" },
    { key: "research", label: "Research needed", type: "textarea" },
    { key: "instructions", label: "Instructions", type: "textarea" },
    { key: "reviewer_date", label: "Reviewer / Date", type: "text" },
  ],
};

const timelineBlock = {
  title: "Detailed Timeline / Evidence",
  fields: [{ key: "timeline", label: "Date · Event · Evidence · Issue / Follow-up", type: "textarea" }],
};

const spottingBlock = {
  title: "Internal Issue Spotting (attorney review required)",
  fields: [{ key: "issue_spotting", label: "Representative notes", type: "textarea" }],
};

export const STAFF_FORMS = [
  {
    id: "01",
    title: "Client Pre-Intake Questionnaire",
    desc: "Streamlined first-contact questionnaire before the comprehensive legal intake.",
    sections: [
      {
        title: "A. Contact & Identity",
        fields: [
          { key: "full_legal_name", label: "Full legal name", type: "text" },
          { key: "other_names", label: "Other names used", type: "text" },
          { key: "dob", label: "Date of birth", type: "date" },
          { key: "country_of_birth", label: "Country of birth", type: "text" },
          { key: "citizenship", label: "Citizenship", type: "text" },
          { key: "a_number", label: "A-Number", type: "text" },
          { key: "phone", label: "Phone", type: "text" },
          { key: "email", label: "Email", type: "text" },
          { key: "address", label: "Address", type: "textarea" },
          { key: "preferred_language", label: "Preferred language", type: "text" },
        ],
      },
      {
        title: "B. Current Immigration Situation",
        fields: [
          { key: "current_status", label: "Current status (if known)", type: "text" },
          { key: "i94_expiration", label: "I-94 expiration", type: "text" },
          { key: "last_entry_date", label: "Last U.S. entry date", type: "date" },
          { key: "entry_manner", label: "How did you enter?", type: "text" },
          { key: "visa_type", label: "Visa type", type: "text" },
          { key: "pending_case", label: "Pending immigration case?", type: "yesno" },
          { key: "court_case", label: "Immigration court case?", type: "yesno" },
          { key: "next_deadline", label: "Next deadline / hearing", type: "text" },
        ],
      },
      {
        title: "C. What Help Are You Seeking?",
        fields: [
          { key: "main_goal", label: "Main goal", type: "textarea" },
          { key: "sponsor", label: "Who is filing / sponsoring?", type: "text" },
          { key: "relationship_to_sponsor", label: "Relationship to sponsor", type: "text" },
          { key: "urgent_need", label: "Urgent travel / work need", type: "text" },
          { key: "prior_attorney", label: "Prior attorney / representative?", type: "text" },
        ],
      },
      {
        title: "D. Quick Screening",
        fields: [{
          key: "screening_flags", label: "Check any that apply", type: "checks", options: [
            "Ever overstayed or violated status", "Ever entered without inspection",
            "Ever been denied a visa or immigration benefit", "Ever been arrested, cited, charged, or convicted",
            "Ever been ordered removed/deported or missed immigration court",
            "Ever used false documents or information for an immigration purpose",
            "Ever claimed to be a U.S. citizen", "Afraid to return to home country",
            "Victim of crime, abuse, trafficking, or serious exploitation",
            "Has U.S. citizen/LPR spouse, parent, child, stepparent, or close relative",
            "Prior I-130 or other petition filed",
          ],
        }],
      },
      {
        title: "E. Family Snapshot",
        fields: [{ key: "family_snapshot", label: "Name · Relationship · DOB · Citizenship/Status · Country", type: "textarea" }],
      },
      {
        title: "F. Documents Available",
        fields: [{
          key: "documents_available", label: "Check all that apply", type: "checks", options: [
            "Passport", "Visa", "I-94", "Birth certificate", "Marriage/divorce records",
            "USCIS notices", "EAD / green card", "Court / ICE papers", "Police / court records",
            "Prior immigration filings",
          ],
        }],
      },
      {
        title: "Signature",
        fields: [
          { key: "client_signature", label: "Client signature (typed)", type: "text" },
          { key: "signature_date", label: "Date", type: "date" },
        ],
      },
    ],
  },
  {
    id: "02",
    title: "Attorney One-Page Case Review",
    desc: "Rapid supervisory review and disposition sheet.",
    sections: [
      header([
        { key: "supervising_attorney", label: "Supervising attorney", type: "text" },
        { key: "urgency_deadline", label: "Urgency / deadline", type: "text" },
        { key: "current_status", label: "Current status", type: "text" },
        { key: "last_entry", label: "Last entry / manner", type: "text" },
        { key: "current_location", label: "Current location", type: "text" },
        { key: "primary_goal", label: "Primary goal", type: "text" },
      ]),
      { title: "Case in 10 Lines or Less", fields: [{ key: "case_summary", label: "Concise factual summary", type: "textarea" }] },
      { title: "Potential Pathways / Issues", fields: [{ key: "pathways", label: "Pathway · Key supporting facts · Concern / missing fact · Priority", type: "textarea" }] },
      {
        title: "Red Flags",
        fields: [{
          key: "red_flags", label: "Check any that apply", type: "checks", options: [
            "Removal order / court deadline", "Criminal issue", "Fraud / misrepresentation",
            "False U.S.-citizenship issue", "Unlawful presence", "Entry without inspection",
            "Prior removal / reentry", "Asylum timing", "Status expiration", "Child aging out",
            "Prior denial / revocation", "Other",
          ],
        }],
      },
      { title: "Critical Documents Missing", fields: [{ key: "documents_missing", label: "Documents / records needed", type: "textarea" }] },
      {
        title: "Attorney Disposition",
        fields: [
          { key: "primary_strategy", label: "Primary strategy", type: "textarea" },
          { key: "alternative_strategy", label: "Alternative strategy", type: "textarea" },
          { key: "research_needed", label: "Research needed", type: "textarea" },
          { key: "evidence_needed", label: "Evidence needed", type: "textarea" },
          { key: "approved_to_prepare", label: "Approved to prepare?", type: "select", options: ["Yes", "No", "Hold"] },
          { key: "approved_forms", label: "Approved form(s) / matter", type: "text" },
          { key: "hold_reason", label: "Hold / do-not-file reason", type: "textarea" },
          { key: "next_deadline", label: "Next deadline", type: "date" },
          { key: "attorney_instructions", label: "Attorney instructions", type: "textarea" },
          { key: "attorney_signature", label: "Attorney / Date", type: "text" },
        ],
      },
    ],
  },
  {
    id: "03",
    title: "Family-Based / AOS / Consular Supplement",
    desc: "Case-type supplement to the Master Immigration Intake.",
    sections: [
      header(),
      {
        title: "Petitioner & Beneficiary",
        fields: [
          { key: "petitioner_name_status", label: "Petitioner name / status", type: "text" },
          { key: "relationship", label: "Relationship", type: "text" },
          { key: "beneficiary_name", label: "Beneficiary name", type: "text" },
          { key: "priority_date", label: "Priority date", type: "date" },
          { key: "i130_status", label: "I-130 filed / approved?", type: "text" },
          { key: "nvc_case_no", label: "NVC case no.", type: "text" },
          { key: "consular_post", label: "Consular post", type: "text" },
          { key: "aos_status", label: "AOS filed / possible?", type: "text" },
        ],
      },
      {
        title: "Relationship Formation",
        fields: [
          { key: "date_relationship_created", label: "Date relationship legally created", type: "date" },
          { key: "marriage_date_place", label: "Marriage date / place", type: "text" },
          { key: "prior_marriages_terminated", label: "Prior marriages terminated?", type: "text" },
          { key: "step_relationship", label: "Step-relationship date / child age", type: "text" },
          { key: "adoption_custody", label: "Adoption / custody facts", type: "textarea" },
        ],
      },
      {
        title: "AOS / Entry Facts",
        fields: [
          { key: "last_entry", label: "Last entry", type: "date" },
          { key: "inspected", label: "Inspected / admitted / paroled?", type: "text" },
          { key: "status_at_entry", label: "Status at entry", type: "text" },
          { key: "status_expiration", label: "Status expiration", type: "date" },
          { key: "unauthorized_work", label: "Unauthorized work?", type: "yesno" },
          { key: "prior_overstay", label: "Prior overstay?", type: "yesno" },
          { key: "immigrant_intent", label: "Immigrant intent / statements at entry", type: "textarea" },
        ],
      },
      {
        title: "Sponsor",
        fields: [
          { key: "household_size", label: "Household size", type: "text" },
          { key: "income", label: "Income", type: "text" },
          { key: "tax_returns", label: "Tax returns / transcripts", type: "text" },
          { key: "joint_sponsor", label: "Joint sponsor?", type: "yesno" },
          { key: "assets", label: "Assets", type: "text" },
        ],
      },
      timelineBlock, spottingBlock, reviewBlock,
    ],
  },
  {
    id: "04",
    title: "Asylum & Humanitarian Supplement",
    desc: "Case-type supplement to the Master Immigration Intake.",
    sections: [
      header(),
      {
        title: "Timing & Travel",
        fields: [
          { key: "last_arrival", label: "Last U.S. arrival", type: "date" },
          { key: "prior_entries", label: "Prior U.S. entries", type: "textarea" },
          { key: "countries_lived", label: "Countries lived in", type: "text" },
          { key: "status_elsewhere", label: "Status / offer of status elsewhere", type: "text" },
          { key: "prior_asylum", label: "Prior asylum filing", type: "text" },
        ],
      },
      {
        title: "Fear & Harm",
        fields: [
          { key: "who_feared", label: "Who is feared", type: "text" },
          { key: "why_targeted", label: "Why client believes targeted", type: "textarea" },
          { key: "past_harm", label: "Past harm", type: "textarea" },
          { key: "threats", label: "Threats", type: "textarea" },
          { key: "government_involvement", label: "Government involvement / protection", type: "textarea" },
          { key: "internal_relocation", label: "Internal relocation", type: "text" },
        ],
      },
      {
        title: "Other Humanitarian Indicators",
        fields: [
          { key: "crime_victim", label: "Crime victim?", type: "yesno" },
          { key: "law_enforcement", label: "Law enforcement contact?", type: "yesno" },
          { key: "domestic_abuse", label: "Domestic abuse?", type: "yesno" },
          { key: "trafficking", label: "Trafficking / forced labor?", type: "yesno" },
          { key: "child_abuse", label: "Child abuse / neglect?", type: "yesno" },
          { key: "usc_lpr_abuser", label: "U.S. citizen / LPR abuser relationship?", type: "text" },
        ],
      },
      timelineBlock, spottingBlock, reviewBlock,
    ],
  },
  {
    id: "05",
    title: "Naturalization / Citizenship Supplement",
    desc: "Case-type supplement to the Master Immigration Intake.",
    sections: [
      header(),
      {
        title: "LPR / Citizenship Facts",
        fields: [
          { key: "green_card_since", label: "Green card since", type: "date" },
          { key: "category", label: "Category obtained through", type: "text" },
          { key: "spouse_usc", label: "Spouse of U.S. citizen?", type: "yesno" },
          { key: "parents_usc", label: "Parent(s) U.S. citizen?", type: "yesno" },
          { key: "date_parent_naturalized", label: "Date parent naturalized", type: "date" },
          { key: "client_age", label: "Client age at relevant date", type: "text" },
          { key: "current_state_since", label: "Current state since", type: "date" },
        ],
      },
      {
        title: "Residence / Travel",
        fields: [
          { key: "trips_6mo", label: "Trips 6+ months", type: "textarea" },
          { key: "trip_1yr", label: "Trip 1+ year", type: "textarea" },
          { key: "total_days_outside", label: "Total days outside U.S.", type: "text" },
          { key: "reentry_permit", label: "Reentry permit?", type: "yesno" },
        ],
      },
      {
        title: "Good Moral Character / Compliance",
        fields: [
          { key: "taxes_filed", label: "Taxes filed?", type: "yesno" },
          { key: "tax_debt", label: "Tax debt?", type: "yesno" },
          { key: "child_support", label: "Child support current?", type: "text" },
          { key: "selective_service", label: "Selective Service (if applicable)", type: "text" },
          { key: "arrests", label: "Arrests / citations", type: "textarea" },
          { key: "false_claim_voting", label: "False claim / voting issue", type: "textarea" },
          { key: "prior_misrep", label: "Prior immigration misrepresentation", type: "textarea" },
        ],
      },
      timelineBlock, spottingBlock, reviewBlock,
    ],
  },
  {
    id: "06",
    title: "Waiver / Inadmissibility Supplement",
    desc: "Case-type supplement to the Master Immigration Intake.",
    sections: [
      header(),
      {
        title: "Potential Ground",
        fields: [
          { key: "ground_identified", label: "Ground / issue identified", type: "textarea" },
          { key: "date_event", label: "Date / event", type: "text" },
          { key: "government_finding", label: "Government finding?", type: "text" },
          { key: "prior_waiver", label: "Prior waiver?", type: "text" },
          { key: "removal_history", label: "Removal history", type: "textarea" },
          { key: "unlawful_presence_dates", label: "Unlawful presence dates", type: "textarea" },
        ],
      },
      {
        title: "Qualifying Relatives",
        fields: [
          { key: "usc_lpr_spouse", label: "U.S./LPR spouse", type: "text" },
          { key: "usc_lpr_parent", label: "U.S./LPR parent", type: "text" },
          { key: "other_relative", label: "Other relevant relative", type: "text" },
          { key: "relationship_evidence", label: "Relationship evidence", type: "textarea" },
        ],
      },
      {
        title: "Hardship Development",
        fields: [
          { key: "hardship_medical", label: "Medical", type: "textarea" },
          { key: "hardship_financial", label: "Financial", type: "textarea" },
          { key: "hardship_educational", label: "Educational", type: "textarea" },
          { key: "hardship_country", label: "Country conditions", type: "textarea" },
          { key: "hardship_separation", label: "Family separation", type: "textarea" },
          { key: "hardship_caregiving", label: "Caregiving", type: "textarea" },
          { key: "hardship_psychological", label: "Psychological / social", type: "textarea" },
          { key: "hardship_other", label: "Other", type: "textarea" },
        ],
      },
      timelineBlock, spottingBlock, reviewBlock,
    ],
  },
  {
    id: "07",
    title: "EOIR / Removal Defense Supplement",
    desc: "Case-type supplement to the Master Immigration Intake.",
    sections: [
      header(),
      {
        title: "Proceedings",
        fields: [
          { key: "court", label: "Court", type: "text" },
          { key: "judge", label: "Judge", type: "text" },
          { key: "next_hearing", label: "Next hearing", type: "date" },
          { key: "nta_date", label: "NTA date", type: "date" },
          { key: "nta_charges", label: "NTA charges", type: "textarea" },
          { key: "pleadings", label: "Pleadings taken?", type: "text" },
          { key: "prior_orders", label: "Prior orders", type: "textarea" },
          { key: "appeal_motion", label: "Appeal / motion", type: "text" },
        ],
      },
      {
        title: "Custody / ICE",
        fields: [
          { key: "detained", label: "Detained?", type: "yesno" },
          { key: "facility", label: "Facility", type: "text" },
          { key: "bond", label: "Bond", type: "text" },
          { key: "ice_checkin", label: "ICE check-in", type: "text" },
          { key: "order_of_supervision", label: "Order of supervision", type: "text" },
        ],
      },
      {
        title: "Relief Screening",
        fields: [
          { key: "family_relationships", label: "Family relationships", type: "textarea" },
          { key: "residence_history", label: "Residence history", type: "textarea" },
          { key: "fear_of_return", label: "Fear of return", type: "textarea" },
          { key: "victim_abuse", label: "Victim / abuse facts", type: "textarea" },
          { key: "lpr_history", label: "LPR history", type: "text" },
          { key: "criminal_history", label: "Criminal history", type: "textarea" },
          { key: "prior_applications", label: "Prior applications", type: "textarea" },
        ],
      },
      timelineBlock, spottingBlock, reviewBlock,
    ],
  },
];

export const FORM_BY_ID = Object.fromEntries(STAFF_FORMS.map((f) => [f.id, f]));

export const SOP = {
  title: "Digital Case File & Workflow SOP",
  desc: "Standardized organization, naming, review, and quality-control protocol.",
  folders: [
    "00_ADMIN_CONFLICT_ENGAGEMENT", "01_IDENTITY_CIVIL_DOCUMENTS", "02_IMMIGRATION_HISTORY_I94_VISAS",
    "03_PRIOR_FILINGS_USCIS_NVC", "04_FAMILY_RELATIONSHIP", "05_FINANCIAL_SPONSOR",
    "06_CRIMINAL_COURT_RECORDS", "07_EOIR_ICE", "08_HUMANITARIAN_EVIDENCE", "09_DRAFT_FORMS",
    "10_ATTORNEY_REVIEW", "11_SIGNED_FINAL", "12_FILED_RECEIPTS", "13_CORRESPONDENCE",
    "14_NOTES_RESEARCH", "15_CLOSING",
  ],
  naming: "YYYY-MM-DD_ClientLast_ClientFirst_DocumentType_Description_Status.ext — e.g. 2026-09-02_Smith_Ana_I94_MostRecent_SOURCE.pdf",
  namingRules: [
    "Never overwrite source evidence",
    "Use DRAFT, ATTY-REVIEW, APPROVED, SIGNED, FILED as controlled status labels",
    "Keep attorney instructions in the attorney-review folder",
    "Do not mix one family member's A-file records with another's without clear labels",
    "Preserve original-language document and translation together",
    "Track expiration dates and filing deadlines separately",
  ],
  stages: [
    ["1 Pre-intake", "Intake staff", "Pre-intake + conflict data", "As policy requires"],
    ["2 Full intake", "Representative / staff", "Master intake + chronology", "No filing"],
    ["3 Evidence collection", "Staff / client", "Indexed evidence", "No filing"],
    ["4 Issue spotting", "AR / trainee", "Case review summary", "YES"],
    ["5 Attorney review", "Attorney", "Strategy / instructions", "YES"],
    ["6 Form preparation", "Authorized team", "Draft packet", "YES before filing"],
    ["7 Client verification", "Client / team", "Corrections / signatures", "YES as required"],
    ["8 Filing", "Authorized practitioner", "Filed copy + proof", "YES"],
    ["9 Post-filing", "Team", "Receipts / RFE / interview / hearing tracking", "Escalate legal issues"],
    ["10 Closing", "Team", "Closing checklist + retention", "As policy requires"],
  ],
  qcChecklist: [
    "Names / DOB / A-number consistent", "Entry history reconciled with I-94 / passport",
    "All prior filings identified", "Immigration court status addressed",
    "Criminal questions separately answered", "Family relationships diagrammed",
    "Missing evidence list current", "Chronology complete", "Red flags escalated",
    "Client goals clearly stated",
  ],
  confidentiality: "Limit access to personnel who need the information for the matter. Follow the supervising attorney's and Recognized Organization's policies for confidentiality, secure transmission, retention, destruction, and incident response.",
};

export const LOG_TYPES = [
  {
    id: "training",
    title: "Formal Training",
    fields: [
      { key: "date", label: "Date", type: "date" },
      { key: "training", label: "Training", type: "text" },
      { key: "provider", label: "Provider", type: "text" },
      { key: "presenter", label: "Presenter", type: "text" },
      { key: "topics", label: "Topics", type: "textarea" },
      { key: "hours", label: "Hours", type: "text" },
      { key: "ethics_hours", label: "Ethics hours", type: "text" },
      { key: "format", label: "Web / in person", type: "select", options: ["Web", "In person", "Hybrid"] },
      { key: "verifiable_record", label: "Verifiable record (certificate, link)", type: "text" },
    ],
  },
  {
    id: "experience",
    title: "Hands-On Immigration Experience",
    fields: [
      { key: "date", label: "Date", type: "date" },
      { key: "matter_type", label: "Matter type", type: "text" },
      { key: "task", label: "Task performed", type: "textarea" },
      { key: "legal_or_admin", label: "Legal / admin", type: "select", options: ["Immigration-law", "Administrative", "Mixed"] },
      { key: "hours", label: "Hours", type: "text" },
      { key: "supervisor", label: "Supervisor", type: "text" },
      { key: "onsite_remote", label: "On-site / remote", type: "select", options: ["On-site", "Remote"] },
      { key: "feedback", label: "Feedback / outcome", type: "textarea" },
    ],
  },
  {
    id: "eoir",
    title: "EOIR / Advocacy Experience",
    fields: [
      { key: "date", label: "Date", type: "date" },
      { key: "court_matter", label: "Court / matter", type: "text" },
      { key: "role", label: "Observed / assisted / performed", type: "select", options: ["Observed", "Assisted", "Performed"] },
      { key: "skill", label: "Skill", type: "text" },
      { key: "hours", label: "Hours", type: "text" },
      { key: "supervisor", label: "Supervisor", type: "text" },
      { key: "notes", label: "Work product / notes", type: "textarea" },
    ],
  },
];
